import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import Wordmark from '@/components/brand/wordmark'
import LocalTime from '@/components/generals/local-time'
import RollText from '@/components/generals/roll-text'
import TransitionLink from '@/components/motion/transition-link'
import { useLenis } from '@/components/motion/smooth-scroll'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'
import { contact, footerTagline, navItems } from '@/constants/site'

// On wider screens the footer sits fixed underneath the page and is uncovered
// as the last section scrolls away (the wrapper's clip-path limits it to its
// own box). The wordmark letters rise in step with that reveal.
const Footer: FC = (): ReactNode => {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const lenis = useLenis()

    useGSAP(() => {
        const wrapper = wrapperRef.current

        if (!wrapper) return

        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.motion, () => {
            gsap.from(wrapper.querySelectorAll('.wordmark-letter'), {
                y: 520,
                ease: 'none',
                stagger: 0.05,
                scrollTrigger: {
                    trigger: wrapper,
                    start: 'top bottom',
                    end: 'bottom bottom',
                    scrub: 0.6
                }
            })
        })

        return () => mm.revert()
    }, { scope: wrapperRef })

    const backToTop = (): void => {
        if (lenis) lenis.scrollTo(0, { duration: 1.8 })
        else window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <div
            ref={wrapperRef}
            className='relative md:h-[100svh] md:min-h-[720px]'
            style={{ clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' }}
        >
            <footer
                data-theme='ink'
                className='relative flex w-full flex-col justify-between gap-16 bg-canvas px-5 pb-6 pt-24 text-fore md:fixed md:bottom-0 md:left-0 md:h-[100svh] md:min-h-[720px] md:px-8 md:pt-28'
            >
                <div className='grid gap-12 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <p className='type-h3 max-w-[22ch]'>
                            {footerTagline}
                        </p>

                        <TransitionLink
                            href='/contact'
                            className='group mt-8 inline-flex items-center gap-3 bg-volt px-5 py-4 text-sm font-medium text-ink'
                        >
                            <RollText>
                                Start a project
                            </RollText>

                            <ArrowUpRight aria-hidden='true' className='size-4' />
                        </TransitionLink>
                    </div>

                    <div className='grid grid-cols-2 gap-10 sm:grid-cols-4 lg:col-span-7'>
                        <div className='space-y-3'>
                            <p className='type-label text-quiet'>
                                Studios
                            </p>

                            <ul className='space-y-2'>
                                {contact.studios.map(studio => (
                                    <li key={studio.city}>
                                        <p>
                                            {studio.city}
                                        </p>

                                        <p className='type-label text-quiet'>
                                            {studio.country}, <LocalTime zone={studio.zone} />
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='space-y-3'>
                            <p className='type-label text-quiet'>
                                Say hello
                            </p>

                            <ul className='space-y-2'>
                                <li>
                                    <a href={`mailto:${contact.email}`} className='link-line break-all'>
                                        {contact.email}
                                    </a>
                                </li>

                                <li>
                                    <a href={contact.phoneHref} className='link-line'>
                                        {contact.phone}
                                    </a>
                                </li>

                                <li>
                                    <a href={contact.calendly} target='_blank' rel='noreferrer' className='link-line'>
                                        Book a discovery call
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className='space-y-3'>
                            <p className='type-label text-quiet'>
                                Sitemap
                            </p>

                            <ul className='space-y-2'>
                                {[{ label: 'Home', href: '/' }, ...navItems, { label: 'Privacy', href: '/privacy' }].map(item => (
                                    <li key={item.href}>
                                        <TransitionLink href={item.href} className='group inline-flex'>
                                            <RollText>
                                                {item.label}
                                            </RollText>
                                        </TransitionLink>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='space-y-3'>
                            <p className='type-label text-quiet'>
                                Follow
                            </p>

                            <ul className='space-y-2'>
                                {contact.socials.map(social => (
                                    <li key={social.label}>
                                        <a href={social.href} target='_blank' rel='noreferrer' className='group inline-flex'>
                                            <RollText>
                                                {social.label}
                                            </RollText>
                                        </a>
                                    </li>
                                ))}

                                <li>
                                    <a href={contact.clutch} target='_blank' rel='noreferrer' className='group inline-flex'>
                                        <RollText>
                                            Review us on Clutch
                                        </RollText>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div>
                    <Wordmark className='text-stone' />

                    <div className='type-label mt-5 flex flex-wrap items-center justify-between gap-4 border-t border-rule pt-5 text-quiet'>
                        <p>
                            © 2026 Fit Design. All rights reserved.
                        </p>

                        <p>
                            Solencer Ltd, trading as Fit Design
                        </p>

                        <button type='button' onClick={backToTop} className='group inline-flex items-center gap-2 text-fore'>
                            <RollText>
                                Back to top
                            </RollText>

                            <ArrowUp aria-hidden='true' className='size-3.5' />
                        </button>
                    </div>
                </div>
            </footer>
        </div>
    )
}

export default Footer
